import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronRight, Clock, KeyRound, Loader2, MessageSquare, Shield, ShieldAlert, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PANEL_LOADER_MIN_MS } from "@/panel/panelLoader";

const otpClient = axios.create({ withCredentials: true });

/**
 * OTP kanal seçimi + 6 haneli doğrulama
 * - X ile kapatma oturumu silmez (kaldığı yerden devam)
 * - İptal yalnızca açık onay ile
 * - 5 hatalı deneme sonrası geçici blok
 */
const LoginOtpPanels = ({
  apiBase,
  creds,
  isDark,
  panelLoader,
  onComplete,
  onDismiss,
  initialChooseChannel = false,
  initialChannels = null,
  initialPhoneMasked = "",
  initialOtpState = null,
  initialBlocked = false,
  initialRetryIn = 0,
  rememberDays = 0,
  deviceLabel = "",
}) => {
  const [step, setStep] = useState(() => {
    if (initialBlocked) return "blocked";
    const hasActiveOtp = initialOtpState?.otp_required && (
      initialOtpState?.channel ||
      initialOtpState?.resumed ||
      !initialChooseChannel
    );
    if (hasActiveOtp) return "otp";
    if (initialOtpState?.choose_phone) return "phone";
    if (initialChooseChannel) return "channel";
    return "hidden";
  });
  const [phoneMasked, setPhoneMasked] = useState(initialPhoneMasked || initialOtpState?.phone_masked || "");
  const [phones, setPhones] = useState(initialOtpState?.phones || []);
  const [pendingChannel, setPendingChannel] = useState(initialOtpState?.channel || "sms");
  const [channels, setChannels] = useState(initialChannels || initialOtpState?.channels || { sms: true, whatsapp: false, totp: false });
  const [channel, setChannel] = useState(initialOtpState?.channel || "sms");
  const [channelLabel, setChannelLabel] = useState(initialOtpState?.channel_label || "SMS");
  const [channelErr, setChannelErr] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [resendIn, setResendIn] = useState(initialOtpState?.resend_in || 0);
  const [expiresIn, setExpiresIn] = useState(initialOtpState?.expires_in || 0);
  const [attemptsLeft, setAttemptsLeft] = useState(initialOtpState?.attempts_left ?? null);
  const [blockedIn, setBlockedIn] = useState(initialRetryIn || 0);
  const [channelLoading, setChannelLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [linkEnabled, setLinkEnabled] = useState(!!initialOtpState?.link_enabled);
  const [linkSent, setLinkSent] = useState(!!initialOtpState?.link_sent);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const statusCheckedRef = useRef(false);
  const linkPollRef = useRef(null);

  const postOtp = useCallback(async (action, extra = {}) => {
    const payload = {
      action,
      kullanici_adi: creds.username,
      sifre: creds.password,
      ...extra,
    };
    if (rememberDays > 0 && (action === "verify" || action === "complete_link")) {
      payload.remember_days = rememberDays;
      payload.device_label = deviceLabel;
    }
    const res = await otpClient.post(`${apiBase}/auth/otp`, payload);
    return res.data;
  }, [apiBase, creds.password, creds.username, rememberDays, deviceLabel]);

  const channelLabelFor = (data) => {
    if (data?.channel_label) return data.channel_label;
    if (data?.channel === "whatsapp") return "WhatsApp";
    if (data?.channel === "totp") return "Authenticator";
    return "SMS";
  };

  const applyBlocked = (data) => {
    const retry = data.retry_in ?? 900;
    setBlockedIn(retry);
    setStep("blocked");
    setOtpErr("");
    setChannelErr("");
  };

  const applyOtpState = (data, options = {}) => {
    setPhoneMasked(data.phone_masked || phoneMasked);
    setChannel(data.channel || channel);
    setChannelLabel(channelLabelFor(data));
    setResendIn(data.resend_in ?? 0);
    setExpiresIn(data.expires_in ?? 300);
    if (data.attempts_left != null) setAttemptsLeft(data.attempts_left);
    setLinkEnabled(!!data.link_enabled);
    setLinkSent(!!data.link_sent);
    setStep("otp");
    setDigits(["", "", "", "", "", ""]);
    if (options.toastMessage) {
      toast.info(options.toastMessage);
    }
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  };

  const handleApiError = (err, fallback, target = "otp") => {
    const data = err.response?.data || {};
    if (data.blocked) {
      applyBlocked(data);
      return;
    }
    const message = data.detail || fallback;
    if (target === "channel") setChannelErr(message);
    else setOtpErr(message);
    if (data.attempts_left != null) setAttemptsLeft(data.attempts_left);
  };

  useEffect(() => {
    panelLoader?.hide();
  }, [panelLoader]);

  useEffect(() => {
    if (step === "hidden") return undefined;

    const blockEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("keydown", blockEscape, true);
    return () => window.removeEventListener("keydown", blockEscape, true);
  }, [step]);

  useEffect(() => {
    if (step !== "otp" && step !== "blocked") return undefined;
    timerRef.current = setInterval(() => {
      setResendIn((v) => (v > 0 ? v - 1 : 0));
      setExpiresIn((v) => (v > 0 ? v - 1 : 0));
      setBlockedIn((v) => (v > 0 ? v - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [step]);

  useEffect(() => {
    if (step !== "channel" || statusCheckedRef.current || !creds?.username) return;
    statusCheckedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const data = await postOtp("status");
        if (cancelled) return;
        if (data.blocked) {
          applyBlocked(data);
          return;
        }
        if (data.active && data.channel) {
          applyOtpState(
            { ...data, otp_required: true, resumed: true },
            { toastMessage: data.message || "Mevcut doğrulama oturumunuz devam ediyor." }
          );
        }
      } catch (err) {
        if (!cancelled) handleApiError(err, "Oturum durumu alınamadı.", "channel");
      }
    })();

    return () => { cancelled = true; };
    // applyOtpState / handleApiError: mount-time status sync only when step/creds change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, creds?.username, postOtp]);

  useEffect(() => {
    if (step !== "otp" || !linkEnabled || channel === "totp") {
      if (linkPollRef.current) {
        clearInterval(linkPollRef.current);
        linkPollRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    const pollLinkApproval = async () => {
      try {
        const data = await postOtp("status");
        if (cancelled || !data?.link_approved) return;
        if (linkPollRef.current) {
          clearInterval(linkPollRef.current);
          linkPollRef.current = null;
        }
        setVerifyLoading(true);
        setOtpErr("");
        panelLoader?.show({ message: "Mobil onay alındı, giriş yapılıyor...", fullscreen: true });
        const result = await postOtp("complete_link");
        panelLoader?.hide();
        onComplete(result);
      } catch {
        // polling continues
      } finally {
        if (!cancelled) setVerifyLoading(false);
      }
    };

    linkPollRef.current = setInterval(pollLinkApproval, 2500);
    pollLinkApproval();

    return () => {
      cancelled = true;
      if (linkPollRef.current) {
        clearInterval(linkPollRef.current);
        linkPollRef.current = null;
      }
    };
  }, [step, linkEnabled, channel, postOtp, onComplete, panelLoader]);

  const handleSendChannel = async (selectedChannel) => {
    setChannelLoading(true);
    setChannelErr("");
    try {
      const data = await postOtp("send", { channel: selectedChannel });
      if (data.skip_otp && data.token) {
        onComplete(data);
        return;
      }
      if (data.choose_phone) {
        setPendingChannel(data.channel || selectedChannel);
        setChannelLabel(data.channel_label || channelLabelFor({ channel: selectedChannel }));
        setPhones(data.phones || []);
        setChannel(selectedChannel);
        setStep("phone");
        if (data.message) toast.info(data.message);
        return;
      }
      applyOtpState(data, {
        toastMessage: data.resumed ? (data.message || "Mevcut doğrulama kodunuz geçerli.") : null,
      });
    } catch (err) {
      handleApiError(err, "Kod gönderilemedi.", "channel");
    } finally {
      setChannelLoading(false);
    }
  };

  const handleSelectPhone = async (phoneIndex) => {
    setChannelLoading(true);
    setChannelErr("");
    try {
      const data = await postOtp("send", {
        channel: pendingChannel || channel,
        phone_index: phoneIndex,
      });
      if (data.skip_otp && data.token) {
        onComplete(data);
        return;
      }
      applyOtpState(data, {
        toastMessage: data.message || "Doğrulama kodu gönderildi.",
      });
    } catch (err) {
      handleApiError(err, "Kod gönderilemedi.", "channel");
    } finally {
      setChannelLoading(false);
    }
  };

  const getCode = () => digits.join("");

  const handleVerify = async (codeOverride) => {
    const code = codeOverride || getCode();
    if (code.length !== 6) return;
    setVerifyLoading(true);
    setOtpErr("");
    panelLoader?.show({ message: "Lütfen bekleyiniz", fullscreen: true });
    try {
      const data = await postOtp("verify", { code });
      panelLoader?.hide();
      onComplete(data);
    } catch (err) {
      panelLoader?.hide();
      handleApiError(err, "Doğrulama başarısız.");
      setDigits(["", "", "", "", "", ""]);
      if (step !== "blocked") inputRefs.current[0]?.focus();
    } finally {
      setVerifyLoading(false);
    }
  };

  const applyCodeDigits = (raw) => {
    const code = String(raw || "").replace(/\D/g, "").slice(0, 6);
    if (!code) return;
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < code.length; i += 1) {
      next[i] = code[i];
    }
    setDigits(next);
    const focusIndex = Math.min(code.length, 5);
    setTimeout(() => inputRefs.current[focusIndex]?.focus(), 0);
    if (code.length === 6) {
      handleVerify(code);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    applyCodeDigits(e.clipboardData?.getData("text") || "");
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    setVerifyLoading(true);
    setOtpErr("");
    panelLoader?.show({ message: "Lütfen bekleyiniz", fullscreen: true });
    try {
      const data = await postOtp("resend", { channel });
      panelLoader?.hide();
      window.setTimeout(() => applyOtpState(data, {
        toastMessage: data.resent ? (data.message || "Yeni kod gönderildi.") : null,
      }), PANEL_LOADER_MIN_MS);
    } catch (err) {
      panelLoader?.hide();
      handleApiError(err, "Yeniden gönderilemedi.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length > 1) {
      applyCodeDigits(cleaned);
      return;
    }
    const digit = cleaned.slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    const code = next.join("");
    if (code.length === 6 && next.every(Boolean)) {
      handleVerify(code);
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDismiss = () => {
    setStep("hidden");
    onDismiss?.();
  };

  const handleCancelConfirm = async () => {
    const ok = window.confirm(
      "Doğrulamayı iptal etmek istediğinize emin misiniz?\n\nGönderilen kod geçersiz olur ve yeni kod istemeniz gerekir."
    );
    if (!ok) return;
    try {
      await postOtp("cancel");
    } catch {
      // ignore
    }
    statusCheckedRef.current = false;
    setStep("hidden");
    onDismiss?.();
    toast.message("Doğrulama iptal edildi.");
  };

  if (step === "hidden") return null;

  const theme = isDark ? "dark" : "light";
  const overlayClass = `login-otp-overlay fixed inset-0 flex items-center justify-center p-4 backdrop-blur-md ${isDark ? "bg-slate-950/70" : "bg-slate-900/35"}`;
  const cardClass = `login-otp-card login-otp-card--${theme} w-full max-w-lg`;
  const headerClass = `login-otp-header login-otp-header--${theme}`;
  const glowClass = `login-otp-header-glow login-otp-header-glow--${theme}`;
  const iconWrapClass = `login-otp-icon-wrap login-otp-icon-wrap--${theme}`;
  const closeClass = `login-otp-close login-otp-close--${theme}`;
  const phonePillClass = `login-otp-phone-pill login-otp-phone-pill--${theme}`;
  const mutedClass = isDark ? "text-slate-400" : "text-slate-500";
  const titleClass = isDark ? "text-white" : "text-slate-900";

  const channelOptions = [
    channels.totp && {
      id: "totp",
      className: "login-otp-channel-btn--totp",
      Icon: KeyRound,
      title: "Authenticator",
      sub: "Google / Microsoft Authenticator",
    },
    channels.whatsapp && {
      id: "whatsapp",
      className: "login-otp-channel-btn--whatsapp",
      Icon: MessageSquare,
      title: "WhatsApp",
      sub: "Anında mesaj ile doğrulama",
    },
    channels.sms && {
      id: "sms",
      className: "login-otp-channel-btn--sms",
      Icon: Smartphone,
      title: "SMS",
      sub: "Klasik SMS ile doğrulama",
    },
  ].filter(Boolean);

  const channelCount = channelOptions.length;
  const channelGridClass = `login-otp-channel-grid login-otp-channel-grid--n${Math.min(Math.max(channelCount, 1), 3)}`;

  const isTotpChannel = channel === "totp";

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const phoneChannelLabel = channelLabel || (pendingChannel === "whatsapp" ? "WhatsApp" : "SMS");

  const shellMetaByStep = {
    blocked: {
      titleId: "login-blocked-title",
      title: "Geçici güvenlik kilidi",
      subtitle: "Çok fazla hatalı doğrulama denemesi yapıldı.",
      showCancel: false,
    },
    channel: {
      titleId: "login-channel-title",
      title: "Doğrulama yöntemi",
      subtitle: phoneMasked ? "Doğrulama kodunu almak istediğiniz kanalı seçin." : "Kod gönderim yöntemini seçin.",
      showCancel: true,
    },
    phone: {
      titleId: "login-phone-title",
      title: "Telefon seçimi",
      subtitle: `${phoneChannelLabel} ile doğrulama kodunun gönderileceği numarayı seçin.`,
      showCancel: true,
    },
    otp: {
      titleId: "login-otp-title",
      title: "Doğrulama kodu",
      subtitle: isTotpChannel
        ? "Authenticator uygulamanızdaki 6 haneli kodu girin."
        : `${channelLabel} ile gönderilen 6 haneli kodu girin.`,
      showCancel: true,
    },
  };
  const shellMeta = shellMetaByStep[step] || shellMetaByStep.otp;

  const channelBadgeVariant = channel === "whatsapp" ? "whatsapp" : channel === "totp" ? "totp" : "sms";
  const channelBadgeClass = `login-otp-channel-badge login-otp-channel-badge--${channelBadgeVariant} ${isDark ? "login-otp-channel-badge--dark" : ""}`;
  const timerClass = `login-otp-timer-pill login-otp-timer-pill--${theme}`;
  const digitClass = (filled) => `login-otp-digit login-otp-digit--${theme}${filled ? " login-otp-digit--filled" : ""}`;

  return (
    <div className={overlayClass} role="dialog" aria-modal="true" aria-labelledby={shellMeta.titleId}>
      <div className={cardClass}>
        <div className={headerClass}>
          <div className={glowClass} aria-hidden="true" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={iconWrapClass}>
                <Shield className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 id={shellMeta.titleId} className={`text-lg sm:text-xl font-semibold tracking-tight ${titleClass}`}>{shellMeta.title}</h2>
                <p className={`text-sm mt-1 leading-relaxed ${mutedClass}`}>{shellMeta.subtitle}</p>
                {phoneMasked && step !== "blocked" && step !== "phone" && (
                  <div className={phonePillClass}>
                    <Smartphone className="w-3.5 h-3.5 shrink-0 opacity-80" />
                    <span className="font-mono tracking-wide">{phoneMasked}</span>
                  </div>
                )}
              </div>
            </div>
            <button type="button" onClick={handleDismiss} className={closeClass} aria-label="Kapat (oturum devam eder)" title="Kapat — kod geçerli kalır">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="login-otp-body">
          {step === "blocked" && (
            <div className={`rounded-xl border p-4 flex items-start gap-3 ${isDark ? "border-rose-500/30 bg-rose-500/10" : "border-rose-200 bg-rose-50"}`}>
              <ShieldAlert className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm ${isDark ? "text-rose-200" : "text-rose-800"}`}>
                  Güvenlik nedeniyle doğrulama geçici olarak engellendi.
                </p>
                {blockedIn > 0 ? (
                  <p className={`text-sm mt-2 font-mono tabular-nums ${mutedClass}`}>
                    Tekrar deneme: <strong className={isDark ? "text-white" : "text-gray-900"}>{formatCountdown(blockedIn)}</strong>
                  </p>
                ) : (
                  <p className={`text-sm mt-2 ${mutedClass}`}>Kilidi kaldırmak için tekrar giriş yapabilirsiniz.</p>
                )}
              </div>
            </div>
          )}

          {step === "channel" && (
            <>
              {channelErr && <div className={`login-otp-error login-otp-error--${theme}`}>{channelErr}</div>}
              <div className={channelGridClass}>
                {channelOptions.map((option) => {
                  const OptionIcon = option.Icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={channelLoading}
                      onClick={() => handleSendChannel(option.id)}
                      className={`login-otp-channel-btn ${option.className}`}
                    >
                      <span className="login-otp-channel-icon">
                        {channelLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <OptionIcon className="w-5 h-5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="login-otp-channel-title">{option.title}</span>
                        <span className="login-otp-channel-sub">{option.sub}</span>
                      </span>
                      <ChevronRight className="login-otp-channel-arrow w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === "phone" && (
            <>
              {channelErr && <div className={`login-otp-error login-otp-error--${theme}`}>{channelErr}</div>}
              <div className="space-y-2">
                {(phones || []).map((item, index) => {
                  const phoneIndex = item.index ?? index;
                  const masked = item.phone_masked || "0*** *** ** **";
                  return (
                    <button
                      key={`phone-${phoneIndex}`}
                      type="button"
                      disabled={channelLoading}
                      onClick={() => handleSelectPhone(phoneIndex)}
                      className={`login-otp-channel-btn w-full ${pendingChannel === "whatsapp" ? "login-otp-channel-btn--whatsapp" : "login-otp-channel-btn--sms"}`}
                    >
                      <span className="login-otp-channel-icon">
                        {channelLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
                      </span>
                      <span className="min-w-0 text-left">
                        <span className="login-otp-channel-title font-mono tracking-wide">{masked}</span>
                        <span className="login-otp-channel-sub">Bu numaraya kod gönderilecek</span>
                      </span>
                      <ChevronRight className="login-otp-channel-arrow w-5 h-5" />
                    </button>
                  );
                })}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={channelLoading}
                className={`mt-3 w-full h-9 text-xs ${isDark ? "text-slate-400 hover:text-slate-200" : ""}`}
                onClick={() => {
                  setStep("channel");
                  setChannelErr("");
                }}
              >
                Kanal seçimine dön
              </Button>
            </>
          )}

          {step === "otp" && (
            <>
              <div className={channelBadgeClass}>
                {channel === "whatsapp" ? <MessageSquare className="w-3.5 h-3.5" /> : channel === "totp" ? <KeyRound className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
                {channelLabel}
              </div>

              <div className="login-otp-timer-row">
                {expiresIn > 0 ? (
                  <span className={timerClass}>
                    <Clock className="w-3.5 h-3.5 opacity-80" />
                    Geçerlilik {formatCountdown(expiresIn)}
                  </span>
                ) : (
                  <span className={timerClass}>Kod süresi doldu</span>
                )}
                {!isTotpChannel && resendIn > 0 && (
                  <span className={timerClass}>
                    Yeniden gönder {resendIn}s
                  </span>
                )}
                {attemptsLeft != null && (
                  <span className={timerClass}>
                    Kalan deneme: {attemptsLeft}
                  </span>
                )}
              </div>

              {otpErr && <div className={`login-otp-error login-otp-error--${theme}`}>{otpErr}</div>}

              {linkEnabled && !isTotpChannel && (
                <p className={`text-xs text-center leading-relaxed mb-3 ${isDark ? "text-cyan-300/90" : "text-cyan-700"}`}>
                  Kodu girmek yerine SMS/WhatsApp mesajındaki <strong>hızlı giriş</strong> bağlantısına tıklayarak da onaylayabilirsiniz.
                  {!linkSent && (
                    <span className="block mt-1 opacity-90">Link gelmediyse &quot;Kodu yeniden gönder&quot; ile yeni mesaj isteyin.</span>
                  )}
                </p>
              )}

              <div className="login-otp-digit-grid" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                    onPaste={handlePaste}
                    className={digitClass(!!d)}
                  />
                ))}
              </div>

              <div className="flex flex-col gap-2.5">
                <Button
                  type="button"
                  disabled={verifyLoading || getCode().length !== 6}
                  onClick={() => handleVerify()}
                  className="login-otp-primary-btn"
                >
                  {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Doğrula
                </Button>
                {!isTotpChannel && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={verifyLoading || resendIn > 0}
                    onClick={handleResend}
                    className={`h-10 rounded-xl ${mutedClass} ${isDark ? "hover:bg-slate-800/80 hover:text-slate-200" : "hover:bg-slate-100 hover:text-slate-800"}`}
                  >
                    {resendIn > 0 ? `Yeniden göndermek için ${resendIn}s bekleyin` : "Kodu yeniden gönder"}
                  </Button>
                )}
              </div>
            </>
          )}

          {shellMeta.showCancel && (
            <div className="mt-4 pt-3 border-t border-dashed border-slate-500/20">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancelConfirm}
                className={`w-full h-9 text-xs ${isDark ? "text-rose-300 hover:bg-rose-500/10 hover:text-rose-200" : "text-rose-600 hover:bg-rose-50"}`}
              >
                Doğrulamayı iptal et
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginOtpPanels;
